import React, { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/Button';
import { listOwnShareGrants, listReceivedShareGrants, getShareGrant, getShareGrantHistory, revokeShareGrant } from '../api/sharing';
import { decryptShareGrant } from '../utils/shareCrypto';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { Copy, Eye, EyeOff, Check, X, Shield, LockKeyhole, ExternalLink, Globe } from 'lucide-react';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const parsePermissions = (perm) => {
  if (!perm) return ['VIEW'];
  if (Array.isArray(perm)) return perm.map((p) => String(p).toUpperCase());
  try {
    const parsed = JSON.parse(perm);
    if (Array.isArray(parsed)) return parsed.map((p) => String(p).toUpperCase());
    if (typeof parsed === 'string') return [parsed.toUpperCase()];
  } catch {
    return [String(perm).toUpperCase()];
  }
  return ['VIEW'];
};

export default function Shares() {
  const location = useLocation();
  const { vaultKey } = useAuth();
  const requestedTab = new URLSearchParams(location.search).get('tab');
  const initialTab = location.pathname === '/vault/shared-with-me' || requestedTab === 'received' ? 'received' : 'owned';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [ownedShares, setOwnedShares] = useState([]);
  const [receivedShares, setReceivedShares] = useState([]);
  const [selectedGrantId, setSelectedGrantId] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Viewing & Copying states
  const [copyingId, setCopyingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [viewingGrant, setViewingGrant] = useState(null);
  const [viewingData, setViewingData] = useState(null);
  const [viewingLoading, setViewingLoading] = useState(false);
  const [viewingError, setViewingError] = useState('');
  const [showViewPassword, setShowViewPassword] = useState(false);
  const [modalCopiedField, setModalCopiedField] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const showToast = (msg) => {
    setToastMessage(msg);
    window.setTimeout(() => setToastMessage(''), 2500);
  };

  const grants = useMemo(() => (activeTab === 'owned' ? ownedShares : receivedShares), [activeTab, ownedShares, receivedShares]);
  const selectedGrant = grants.find((grant) => grant.id === selectedGrantId) || null;

  useEffect(() => {
    setActiveTab(location.pathname === '/vault/shared-with-me' || new URLSearchParams(location.search).get('tab') === 'received' ? 'received' : 'owned');
  }, [location.pathname, location.search]);

  const loadShares = async () => {
    try {
      const [own, received] = await Promise.all([listOwnShareGrants(), listReceivedShareGrants()]);
      setOwnedShares(own);
      setReceivedShares(received);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShares();
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      if (!selectedGrantId) {
        setHistory([]);
        return;
      }

      setHistoryLoading(true);
      try {
        const events = await getShareGrantHistory(selectedGrantId);
        setHistory(events);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
  }, [selectedGrantId]);

  const handleRevoke = async (grantId) => {
    await revokeShareGrant(grantId);
    await loadShares();
    showToast('Share grant revoked.');
  };

  const handleCopyPassword = async (grant) => {
    setCopyingId(grant.id);
    try {
      const fullGrant = await getShareGrant(grant.id);
      const data = await decryptShareGrant(fullGrant, vaultKey);
      if (!data?.password) {
        throw new Error('No password in this shared item');
      }
      await navigator.clipboard.writeText(data.password);
      setCopiedId(grant.id);
      setTimeout(() => setCopiedId(null), 2000);
      showToast('Password copied to clipboard!');
      // Refresh list to update access count
      loadShares();
    } catch (err) {
      console.error('Failed to copy password:', err);
      alert('Unable to copy password: ' + (err.message || 'Decryption failed'));
    } finally {
      setCopyingId(null);
    }
  };

  const handleView = async (grant) => {
    setViewingGrant(grant);
    setViewingData(null);
    setViewingLoading(true);
    setViewingError('');
    setShowViewPassword(false);
    try {
      const fullGrant = await getShareGrant(grant.id);
      const data = await decryptShareGrant(fullGrant, vaultKey);
      setViewingData(data);
      // Refresh list to update access count
      loadShares();
    } catch (err) {
      console.error('Failed to view grant:', err);
      setViewingError(err.message || 'Unable to decrypt shared item');
    } finally {
      setViewingLoading(false);
    }
  };

  const handleModalCopy = async (field, val) => {
    try {
      await navigator.clipboard.writeText(val || '');
      setModalCopiedField(field);
      setTimeout(() => setModalCopiedField(''), 2000);
    } catch { /* ignore */ }
  };

  return (
    <MainLayout>
      <div className="page-header shares-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="page-title notes-hero-title" style={{ margin: 0 }}>Shares</h1>
            <p className="notes-hero-subtitle" style={{ marginTop: '4px' }}>Manage temporary shares, expirations, and access history.</p>
          </div>
          <Link to="/vault" className="shares-back-link btn btn-secondary notes-hero-btn-secondary" style={{ padding: '8px 16px', borderRadius: '8px', textDecoration: 'none' }}>← Back to vault</Link>
        </div>
      </div>

      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: 'rgba(15, 23, 42, 0.92)',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 9999,
          fontSize: '14px',
          backdropFilter: 'blur(8px)',
        }}>
          <Check size={16} color="#22c55e" />
          {toastMessage}
        </div>
      )}

      <div className="shares-tabs-row">
        <Button variant={activeTab === 'owned' ? 'primary' : 'ghost'} onClick={() => setActiveTab('owned')}>Owned shares</Button>
        <Button variant={activeTab === 'received' ? 'primary' : 'ghost'} onClick={() => setActiveTab('received')}>Received shares</Button>
      </div>

      {loading ? (
        <div className="glass-panel" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading shares...</div>
      ) : (
        <div className="shares-layout-grid">
          <div className="glass-panel shares-table-card">
            <div className="shares-table-header">
              {activeTab === 'owned' ? 'Shares you created' : 'Shares shared with you'}
            </div>
            <div className="shares-table-wrap">
              <table className="shares-table">
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                    <th>Item</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Access</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grants.map((grant) => {
                    const permissions = parsePermissions(grant.permission);
                    const canCopy = permissions.includes('COPY');
                    const canView = permissions.includes('VIEW');

                    return (
                      <tr key={grant.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{grant.item_type || grant.itemType || 'Vault Item'}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                            {permissions.join(', ')}
                          </div>
                        </td>
                        <td>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '999px',
                            background: grant.status === 'ACTIVE' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                            color: grant.status === 'ACTIVE' ? '#22c55e' : '#ef4444',
                            fontSize: '11px',
                            fontWeight: 600,
                          }}>
                            {grant.status}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.78rem' }}>{formatDate(grant.expiresAt)}</td>
                        <td style={{ fontSize: '0.78rem' }}>
                          {grant.accessCount || 0}{grant.maxAccessCount ? ` / ${grant.maxAccessCount}` : ''}
                        </td>
                        <td>
                          <div className="shares-actions-wrap" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {activeTab === 'received' && grant.status === 'ACTIVE' && canCopy && (
                              <Button
                                type="button"
                                variant="primary"
                                disabled={copyingId === grant.id}
                                onClick={() => handleCopyPassword(grant)}
                                style={{ padding: '5px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
                              >
                                {copiedId === grant.id ? <Check size={13} color="#22c55e" /> : <Copy size={13} />}
                                {copiedId === grant.id ? 'Copied!' : copyingId === grant.id ? 'Copying...' : 'Copy'}
                              </Button>
                            )}
                            {activeTab === 'received' && grant.status === 'ACTIVE' && canView && (
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => handleView(grant)}
                                style={{ padding: '5px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
                              >
                                <Eye size={13} /> View
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => { setSelectedGrantId(grant.id); }}
                              style={{ padding: '5px 8px', fontSize: '12px' }}
                            >
                              History
                            </Button>
                            {activeTab === 'owned' && grant.status === 'ACTIVE' && (
                              <Button
                                type="button"
                                variant="danger"
                                onClick={() => handleRevoke(grant.id)}
                                style={{ padding: '5px 8px', fontSize: '12px' }}
                              >
                                Revoke
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {grants.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ padding: '36px 16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.84rem' }}>
                        No shares found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="glass-panel shares-history-card">
            <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '0.94rem' }}>Access history</div>
            {!selectedGrant ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Select &quot;History&quot; on any share to view its access trail.</div>
            ) : historyLoading ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Loading history...</div>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ padding: '10px 12px', borderRadius: '10px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
                  <div style={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', fontSize: '0.86rem' }}>
                    <span>{selectedGrant.item_type || 'Vault Item'}</span>
                    <span style={{ color: selectedGrant.status === 'ACTIVE' ? '#22c55e' : '#ef4444', fontSize: '11px', fontWeight: 600 }}>
                      {selectedGrant.status}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px' }}>
                    Permissions: {parsePermissions(selectedGrant.permission).join(', ')} · Expires {formatDate(selectedGrant.expiresAt)}
                  </div>
                </div>

                {history.map((event) => (
                  <div key={event.id} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                      <strong style={{ fontSize: '0.82rem' }}>{event.action}</strong>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>{formatDate(event.created_at)}</span>
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px' }}>
                      {event.user_email || 'System'} {event.metadata?.reason ? `· ${event.metadata.reason}` : ''}
                    </div>
                  </div>
                ))}

                {history.length === 0 && <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>No share events recorded yet.</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* VIEW SHARED ITEM MODAL */}
      {viewingGrant && (
        <div className="shares-modal-overlay">
          <div className="glass-panel shares-modal-card">
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 18px',
              borderBottom: '1px solid var(--border-color)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LockKeyhole size={18} color="var(--accent-primary)" />
                <h3 style={{ margin: 0, fontSize: '17px' }}>
                  {viewingData?.title || viewingData?.name || viewingGrant.item_type || 'Shared Item'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setViewingGrant(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {viewingLoading ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-secondary)' }}>
                  Decrypting shared vault item...
                </div>
              ) : viewingError ? (
                <div style={{ color: 'var(--danger)', fontSize: '14px', background: 'rgba(239,68,68,0.1)', padding: '12px', borderRadius: '8px' }}>
                  ⚠ {viewingError}
                </div>
              ) : viewingData ? (
                <>
                  {viewingData.username && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Username</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: '8px' }}>
                        <span style={{ flex: 1, fontSize: '14px' }}>{viewingData.username}</span>
                        <button
                          type="button"
                          onClick={() => handleModalCopy('username', viewingData.username)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                          title="Copy username"
                        >
                          {modalCopiedField === 'username' ? <Check size={16} color="#22c55e" /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>
                  )}

                  {viewingData.password && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: '8px' }}>
                        <span style={{
                          flex: 1,
                          fontFamily: 'monospace',
                          fontSize: '15px',
                          filter: showViewPassword ? 'none' : 'blur(5px)',
                          userSelect: showViewPassword ? 'auto' : 'none',
                          transition: 'filter 0.2s',
                        }}>
                          {viewingData.password}
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowViewPassword(p => !p)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                        >
                          {showViewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleModalCopy('password', viewingData.password)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                          title="Copy password"
                        >
                          {modalCopiedField === 'password' ? <Check size={16} color="#22c55e" /> : <Copy size={16} />}
                        </button>
                      </div>
                      {modalCopiedField === 'password' && <span style={{ fontSize: '12px', color: '#22c55e' }}>Copied to clipboard!</span>}
                    </div>
                  )}

                  {viewingData.url && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Website</label>
                      <a href={viewingData.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Globe size={14} /> {viewingData.url}
                      </a>
                    </div>
                  )}

                  {viewingData.notes && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notes</label>
                      <div style={{ background: 'var(--bg-secondary)', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>
                        {viewingData.notes}
                      </div>
                    </div>
                  )}

                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', borderTop: '1px solid var(--border-color)', paddingTop: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Expires: {formatDate(viewingGrant.expiresAt)}</span>
                    <span>Access count: {viewingGrant.accessCount || 0}{viewingGrant.maxAccessCount ? ` / ${viewingGrant.maxAccessCount}` : ''}</span>
                  </div>
                </>
              ) : null}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', background: 'var(--bg-primary)' }}>
              <Button type="button" variant="ghost" onClick={() => setViewingGrant(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
